import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicator from '@mui/icons-material/DragIndicator';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PushPin from '@mui/icons-material/PushPin';
import ViewColumn from '@mui/icons-material/ViewColumn';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import { paperClasses } from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';

import OutlinedIconButton from '~components/Buttons/OutlinedIconButton';
import Checkbox from '~components/Checkbox/Checkbox';
import { DataTableColumnPin } from '~components/DataTable/dataTableColumn';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

interface DataTableColumnChooserColumn {
  field: string;
  headerName: string;
  isHideable: boolean;
  isVisible: boolean;
  pin?: DataTableColumnPin;
}

interface DataTableColumnChooserProps {
  columns: DataTableColumnChooserColumn[];
  isIconTrigger?: boolean;
  onMoveColumn: (movedField: string, targetField: string) => void;
  onReset: () => void;
  onToggleColumn: (field: string) => void;
}

interface ColumnChooserItemProps {
  column: DataTableColumnChooserColumn;
  onToggleColumn: (field: string) => void;
}

function ColumnChooserItem({ column, onToggleColumn }: ColumnChooserItemProps) {
  const theme = useTheme();
  const isPinned = Boolean(column.pin);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: column.field,
    disabled: isPinned,
  });

  return (
    <Box
      ref={setNodeRef}
      data-test-id={`data-table-column-chooser-${column.field}`}
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        minHeight: 34,
        px: 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {isPinned ? (
        <Box
          aria-label={t('data_table.pinned_column')}
          sx={{ color: 'text.secondary', display: 'flex' }}
        >
          <PushPin sx={{ fontSize: '16px' }} />
        </Box>
      ) : (
        <Box
          {...attributes}
          {...listeners}
          aria-label={t('data_table.reorder_column', { column: column.headerName })}
          sx={{ color: 'text.secondary', cursor: 'grab', display: 'flex' }}
        >
          <DragIndicator sx={{ fontSize: '16px' }} />
        </Box>
      )}
      <FormControlLabel
        label={
          <Typography variant="subtitle2" sx={{ color: theme.brandV2.colors.treadBlack }}>
            {column.headerName}
          </Typography>
        }
        control={
          <Checkbox
            isChecked={column.isVisible}
            isDisabled={!column.isHideable}
            onChange={() => onToggleColumn(column.field)}
            sx={{ mr: 1 }}
          />
        }
        sx={{
          mr: 0,
          overflow: 'hidden',
          '& .MuiFormControlLabel-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </Box>
  );
}

export default function DataTableColumnChooser({
  columns,
  isIconTrigger = false,
  onMoveColumn,
  onReset,
  onToggleColumn,
}: DataTableColumnChooserProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortableFields = useMemo(
    () => columns.filter((column) => !column.pin).map((column) => column.field),
    [columns],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const droppedOnAnotherColumn = over && active.id !== over.id;

      if (droppedOnAnotherColumn) {
        onMoveColumn(String(active.id), String(over.id));
      }
    },
    [onMoveColumn],
  );

  return (
    <Menu
      disableAutoFocusItem
      menuTrigger={
        isIconTrigger ? (
          <OutlinedIconButton aria-label={t('data_table.columns')} size="small">
            <MoreHoriz sx={{ fontSize: '18px' }} />
          </OutlinedIconButton>
        ) : (
          <Button
            color="inherit"
            size="small"
            startIcon={<ViewColumn />}
            sx={{ color: 'text.primary' }}
            variant="text"
          >
            {t('data_table.columns')}
          </Button>
        )
      }
      sx={{ [`& .${paperClasses.root}`]: { width: 280 } }}
    >
      <Box sx={{ maxHeight: 360, overflow: 'auto', px: 0.5 }}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext items={sortableFields} strategy={verticalListSortingStrategy}>
            {columns.map((column) => (
              <ColumnChooserItem
                key={column.field}
                column={column}
                onToggleColumn={onToggleColumn}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
      <Box
        sx={{
          borderColor: 'divider',
          borderTop: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 1,
          pt: 1,
          px: 1.5,
        }}
      >
        <SmallButton
          color="inherit"
          onClick={onReset}
          sx={{ color: 'text.primary' }}
          variant="text"
        >
          {t('actions.reset')}
        </SmallButton>
      </Box>
    </Menu>
  );
}
